import {useOrderChangeContext} from '@hconnect/tuc-processes/orderManagement/orderChange'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useState, useEffect} from 'react'

import {useSubmitOrderChange} from '../../../../common/react-query/hooks/mutations/useSubmitOrderChange'
import {
  ErrorModalState,
  mapTransactionResultToScreenType,
  OrderChangeResultType
} from '../types/orderChangeResult.types'
import {
  getErrorModalVariant,
  getErrorMessage,
  invalidateOrdersCache
} from '../utils/errorHandling.utils'

import {useChangeOrderPolling} from './useChangeOrderPolling'

interface UseChangeOrderOptions {
  /** Callback when order change completes with a result (success or review) */
  onResult?: (resultType: OrderChangeResultType) => void
}

interface UseChangeOrderResult {
  /** Submit the order change request */
  submit: () => void
  /** Whether submission or polling is in progress */
  isLoading: boolean
  /** Error modal state for displaying errors */
  errorModalState: ErrorModalState
  /** Close the error modal */
  closeErrorModal: () => void
  /** Retry the submission (for retryable errors) */
  retrySubmit: () => void
  /** Handle refresh action (for checksum errors) */
  handleRefresh: () => void
}

/**
 * Hook for handling complete order change submission flow.
 *
 * Handles:
 * - Submitting the order change via API
 * - Polling for completion status
 * - Error handling with modal state
 * - Result type determination
 *
 * @example
 * const {submit, isLoading, errorModalState, closeErrorModal} = useChangeOrder({
 *   onResult: (resultType) => goToResultScreen(resultType)
 * })
 */
export const useChangeOrder = (options?: UseChangeOrderOptions): UseChangeOrderResult => {
  const {submit: contextSubmit, processData} = useOrderChangeContext()
  const queryClient = useQueryClient()

  // Request ID state for polling
  const [requestId, setRequestId] = useState<string | undefined>(undefined)

  // Error modal state
  const [errorModalState, setErrorModalState] = useState<ErrorModalState>({
    isOpen: false,
    variant: null,
    message: undefined
  })

  // Mutation for submitting order change
  const {
    mutate,
    isLoading: isMutating,
    reset: resetMutation
  } = useSubmitOrderChange({
    onSuccess: (data) => {
      // Start polling with the returned requestId
      setRequestId(data.requestId)
    },
    onError: (error) => {
      console.log('🚀 ~ useChangeOrder ~ error:', error)
      // Show error modal
      setErrorModalState({
        isOpen: true,
        variant: getErrorModalVariant(error),
        message: getErrorMessage(error)
      })
    }
  })

  // Polling hook
  const {
    triggerPolling,
    isPolling,
    result: pollingResult
  } = useChangeOrderPolling(processData.orderId, requestId)

  // Trigger polling when requestId is set
  useEffect(() => {
    if (requestId) {
      triggerPolling()
    }
  }, [requestId, triggerPolling])

  // Handle polling result
  useEffect(() => {
    if (pollingResult) {
      const resultType = mapTransactionResultToScreenType(pollingResult)
      void queryClient.invalidateQueries({queryKey: ['available-slots']})
      void invalidateOrdersCache(processData.orderId)
      options?.onResult?.(resultType)
      // Reset requestId for potential future submissions
      setRequestId(undefined)
    }
  }, [pollingResult, options, queryClient, processData.orderId])

  // Submit handler
  const submit = useCallback(() => {
    contextSubmit((request) => {
      mutate({
        orderId: processData.orderId,
        payload: request
      })
    })
  }, [contextSubmit, processData.orderId, mutate])

  // Close error modal
  const closeErrorModal = useCallback(() => {
    setErrorModalState({isOpen: false, variant: null, message: undefined})
    resetMutation()
  }, [resetMutation])

  // Retry submission (for retryable errors)
  const retrySubmit = useCallback(() => {
    closeErrorModal()
    submit()
  }, [closeErrorModal, submit])

  // Handle refresh (for checksum errors)
  const handleRefresh = useCallback(() => {
    closeErrorModal()
    void invalidateOrdersCache(processData.orderId)
    // Close the editor - parent component should handle reloading the order
    // The onClose callback should be called from the container
  }, [closeErrorModal, processData.orderId])

  return {
    submit,
    isLoading: isMutating || isPolling,
    errorModalState,
    closeErrorModal,
    retrySubmit,
    handleRefresh
  }
}
